/**
 * INYRON Frame Synchronization Engine
 *
 * A renderer-agnostic continuity authority that sits between gameplay/physics
 * intent and animation presentation. It prevents visible discontinuities by
 * synchronizing normalized phase, playback rate, transition weight, root
 * velocity and yaw rate under bounded per-frame acceleration.
 *
 * The engine never edits a skeleton or bind pose. Renderers consume its output.
 */

export type FrameSyncSemanticFamily =
  | 'idle'
  | 'locomotion'
  | 'air'
  | 'combat'
  | 'reaction'
  | 'interaction'
  | 'rest'
  | 'custom'

export interface FrameSyncVector2 {
  x: number
  z: number
}

export interface FrameSyncInput {
  dt: number
  state: string
  looping: boolean
  /** Cycles per second before playback-rate modulation. */
  baseCyclesPerSecond: number
  targetPlaybackRate: number
  speed01?: number
  grounded?: boolean
  contactPrecision01?: number
  /** Optional phase sampled from an authored clip/mixer, normalized 0..1. */
  authoredPhase01?: number
  /** Gameplay/physics root velocity to reconcile with presentation. */
  rootVelocity?: FrameSyncVector2
  /** Gameplay/physics facing velocity, radians per second. */
  yawRate?: number
}

export interface FrameSyncViolation {
  kind:
    | 'invalid-dt'
    | 'phase-discontinuity'
    | 'rate-discontinuity'
    | 'root-acceleration'
    | 'yaw-acceleration'
  magnitude: number
  limit: number
}

export interface FrameSyncFrame {
  state: string
  family: FrameSyncSemanticFamily
  dt: number
  substeps: number
  phase01: number
  playbackRate: number
  blend01: number
  transitionSeconds: number
  rootVelocity: FrameSyncVector2
  yawRate: number
  stateChanged: boolean
  phasePreserved: boolean
  violations: FrameSyncViolation[]
}

export interface FrameSyncConfig {
  maxFrameDt: number
  fixedSubstep: number
  minTransitionSeconds: number
  maxTransitionSeconds: number
  playbackRateResponse: number
  maxPlaybackRateAccel: number
  maxRootAcceleration: number
  maxYawAcceleration: number
  authoredPhaseCorrectionPerSecond: number
}

export const DEFAULT_FRAME_SYNC_CONFIG: FrameSyncConfig = {
  maxFrameDt: 1 / 15,
  fixedSubstep: 1 / 120,
  minTransitionSeconds: .09,
  maxTransitionSeconds: .36,
  playbackRateResponse: 10,
  maxPlaybackRateAccel: 4.2,
  maxRootAcceleration: 15.5,
  maxYawAcceleration: Math.PI * 6.5,
  authoredPhaseCorrectionPerSecond: .85,
}

const clamp01 = (n: number): number => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0))
const finite = (n: number | null | undefined, fallback = 0): number =>
  typeof n === 'number' && Number.isFinite(n) ? n : fallback
const wrap01 = (n: number): number => {
  const wrapped = finite(n) % 1
  return wrapped < 0 ? wrapped + 1 : wrapped
}

function shortestPhaseDelta(from: number, to: number): number {
  let delta = wrap01(to) - wrap01(from)
  if (delta > .5) delta -= 1
  if (delta < -.5) delta += 1
  return delta
}

function moveTowards(current: number, target: number, maxDelta: number): number {
  if (!Number.isFinite(current)) return finite(target)
  if (!Number.isFinite(target)) return current
  const delta = target - current
  if (Math.abs(delta) <= maxDelta) return target
  return current + Math.sign(delta) * maxDelta
}

function expResponse(current: number, target: number, response: number, dt: number): number {
  const alpha = 1 - Math.exp(-Math.max(0, response) * Math.max(0, dt))
  return current + (target - current) * alpha
}

function smoothstep01(t: number): number {
  const x = clamp01(t)
  return x * x * (3 - 2 * x)
}

export function classifyFrameSyncFamily(state: string): FrameSyncSemanticFamily {
  const s = state.toLowerCase()
  if (/death|hit|stun|knock|hurt|flinch|parry_react/.test(s)) return 'reaction'
  if (/attack|combo|special|ultimate|cast|channel|parry|block|dodge/.test(s)) return 'combat'
  if (/jump|fall|air|apex|descent|glide|fly|flight|hover|dive|plunge|land/.test(s)) return 'air'
  if (/walk|backward|backpedal|retreat|jog|run|sprint|strafe|swim|climb|crawl|move|patrol|migrat/.test(s)) return 'locomotion'
  if (/interact|gesture|touch|gift|aid|use|work/.test(s)) return 'interaction'
  if (/sleep|rest|dormant/.test(s)) return 'rest'
  if (/idle|alert|relaxed|turn_in_place/.test(s)) return 'idle'
  return 'custom'
}

export function frameSyncTransitionSeconds(
  fromState: string | null,
  toState: string,
  speed01 = 0,
  contactPrecision01 = .5,
  config: FrameSyncConfig = DEFAULT_FRAME_SYNC_CONFIG,
): number {
  if (!fromState || fromState === toState) return config.minTransitionSeconds

  const from = classifyFrameSyncFamily(fromState)
  const to = classifyFrameSyncFamily(toState)
  const speed = clamp01(speed01)
  const contact = clamp01(contactPrecision01)

  let seconds = .19
  if (from === 'locomotion' && to === 'locomotion') seconds = .13 + (1 - speed) * .05
  else if ((from === 'idle' && to === 'locomotion') || (from === 'locomotion' && to === 'idle')) seconds = .16
  else if (to === 'reaction') seconds = .09
  else if (to === 'combat') seconds = .115 + (1 - contact) * .04
  else if (from === 'combat' && (to === 'idle' || to === 'locomotion')) seconds = .18
  else if (from === 'air' || to === 'air') seconds = .135 + contact * .05
  else if (to === 'interaction') seconds = .2
  else if (to === 'rest') seconds = .24

  return Math.max(config.minTransitionSeconds, Math.min(config.maxTransitionSeconds, seconds))
}

export function shouldPreserveNormalizedPhase(fromState: string | null, toState: string): boolean {
  if (!fromState) return false
  const from = classifyFrameSyncFamily(fromState)
  const to = classifyFrameSyncFamily(toState)
  if (from === 'locomotion' && to === 'locomotion') return true
  if (from === 'idle' && to === 'idle') return true
  return false
}

export class FrameSynchronizationEngine {
  private readonly config: FrameSyncConfig
  private state: string | null = null
  private family: FrameSyncSemanticFamily = 'idle'
  private phase01 = 0
  private playbackRate = 1
  private rootVelocity: FrameSyncVector2 = { x: 0, z: 0 }
  private yawRate = 0
  private transitionElapsed = 1
  private transitionSeconds = .1
  private initialized = false

  constructor(config?: Partial<FrameSyncConfig>) {
    this.config = { ...DEFAULT_FRAME_SYNC_CONFIG, ...(config ?? {}) }
  }

  reset(state = 'idle', phase01 = 0): void {
    this.state = state
    this.family = classifyFrameSyncFamily(state)
    this.phase01 = wrap01(phase01)
    this.playbackRate = 1
    this.rootVelocity = { x: 0, z: 0 }
    this.yawRate = 0
    this.transitionElapsed = this.config.maxTransitionSeconds
    this.transitionSeconds = this.config.minTransitionSeconds
    this.initialized = false
  }

  snapshot(): FrameSyncFrame {
    return {
      state: this.state ?? 'idle',
      family: this.family,
      dt: 0,
      substeps: 0,
      phase01: this.phase01,
      playbackRate: this.playbackRate,
      blend01: 1,
      transitionSeconds: this.transitionSeconds,
      rootVelocity: { ...this.rootVelocity },
      yawRate: this.yawRate,
      stateChanged: false,
      phasePreserved: false,
      violations: [],
    }
  }

  step(input: FrameSyncInput): FrameSyncFrame {
    const violations: FrameSyncViolation[] = []
    const rawDt = finite(input.dt)
    if (!(rawDt > 0)) {
      violations.push({ kind: 'invalid-dt', magnitude: Math.abs(rawDt), limit: this.config.maxFrameDt })
    }
    const dt = Math.max(0, Math.min(this.config.maxFrameDt, rawDt > 0 ? rawDt : 0))
    const substeps = dt > 0 ? Math.max(1, Math.ceil(dt / this.config.fixedSubstep)) : 0
    const subDt = substeps > 0 ? dt / substeps : 0

    const nextState = String(input.state || 'idle')
    const nextFamily = classifyFrameSyncFamily(nextState)
    const stateChanged = this.state !== nextState
    const phasePreserved = stateChanged && shouldPreserveNormalizedPhase(this.state, nextState)
    const speed01 = clamp01(input.speed01 ?? 0)
    const contact = clamp01(input.contactPrecision01 ?? .5)

    if (!this.initialized) {
      this.state = nextState
      this.family = nextFamily
      this.playbackRate = Math.max(.05, finite(input.targetPlaybackRate, 1))
      this.rootVelocity = {
        x: finite(input.rootVelocity?.x),
        z: finite(input.rootVelocity?.z),
      }
      this.yawRate = finite(input.yawRate)
      this.phase01 = input.authoredPhase01 != null ? wrap01(input.authoredPhase01) : 0
      this.transitionSeconds = this.config.minTransitionSeconds
      this.transitionElapsed = this.transitionSeconds
      this.initialized = true
    } else if (stateChanged) {
      this.transitionSeconds = frameSyncTransitionSeconds(
        this.state,
        nextState,
        speed01,
        contact,
        this.config,
      )
      this.transitionElapsed = 0
      if (!phasePreserved) {
        // Non-loop/semantic transitions start at authored zero. Air/landing
        // states may still provide authoredPhase01, reconciled below.
        this.phase01 = input.authoredPhase01 != null ? wrap01(input.authoredPhase01) : 0
      }
      this.state = nextState
      this.family = nextFamily
    }

    const targetRate = Math.max(.05, finite(input.targetPlaybackRate, 1))
    const targetRootX = finite(input.rootVelocity?.x)
    const targetRootZ = finite(input.rootVelocity?.z)
    const targetYaw = finite(input.yawRate)
    const maxRateStepPerSecond = Math.max(.01, this.config.maxPlaybackRateAccel)
    const maxRootStepPerSecond = Math.max(.01, this.config.maxRootAcceleration)
    const maxYawStepPerSecond = Math.max(.01, this.config.maxYawAcceleration)

    let maxRateObserved = 0
    let maxRootAccelObserved = 0
    let maxYawAccelObserved = 0

    for (let i = 0; i < substeps; i += 1) {
      const rateBefore = this.playbackRate
      const desiredRate = expResponse(
        this.playbackRate,
        targetRate,
        this.config.playbackRateResponse,
        subDt,
      )
      this.playbackRate = moveTowards(
        this.playbackRate,
        desiredRate,
        maxRateStepPerSecond * subDt,
      )
      maxRateObserved = Math.max(maxRateObserved, Math.abs(this.playbackRate - rateBefore) / Math.max(subDt, 1e-6))

      const rootBeforeX = this.rootVelocity.x
      const rootBeforeZ = this.rootVelocity.z
      const rootDeltaX = targetRootX - this.rootVelocity.x
      const rootDeltaZ = targetRootZ - this.rootVelocity.z
      const rootDeltaLength = Math.hypot(rootDeltaX, rootDeltaZ)
      const maxRootDelta = maxRootStepPerSecond * subDt
      if(rootDeltaLength <= maxRootDelta || rootDeltaLength <= 1e-8){
        this.rootVelocity.x = targetRootX
        this.rootVelocity.z = targetRootZ
      }else{
        const rootScale = maxRootDelta / rootDeltaLength
        this.rootVelocity.x += rootDeltaX * rootScale
        this.rootVelocity.z += rootDeltaZ * rootScale
      }
      maxRootAccelObserved = Math.max(
        maxRootAccelObserved,
        Math.hypot(this.rootVelocity.x - rootBeforeX, this.rootVelocity.z - rootBeforeZ) / Math.max(subDt, 1e-6),
      )

      const yawBefore = this.yawRate
      this.yawRate = moveTowards(this.yawRate, targetYaw, maxYawStepPerSecond * subDt)
      maxYawAccelObserved = Math.max(maxYawAccelObserved, Math.abs(this.yawRate - yawBefore) / Math.max(subDt, 1e-6))

      if (input.looping) {
        this.phase01 = wrap01(
          this.phase01 +
          Math.max(0, finite(input.baseCyclesPerSecond)) *
          this.playbackRate *
          subDt,
        )
      } else {
        this.phase01 = Math.min(
          1,
          this.phase01 +
          Math.max(0, finite(input.baseCyclesPerSecond)) *
          this.playbackRate *
          subDt,
        )
      }

      if (input.authoredPhase01 != null && !stateChanged) {
        const authored = wrap01(input.authoredPhase01)
        const delta = input.looping
          ? shortestPhaseDelta(this.phase01, authored)
          : authored - this.phase01
        const maxCorrection = this.config.authoredPhaseCorrectionPerSecond * subDt
        const correction = Math.max(-maxCorrection, Math.min(maxCorrection, delta))
        this.phase01 = input.looping
          ? wrap01(this.phase01 + correction)
          : clamp01(this.phase01 + correction)
      }

      this.transitionElapsed += subDt
    }

    if (maxRateObserved > this.config.maxPlaybackRateAccel * 1.001) {
      violations.push({
        kind: 'rate-discontinuity',
        magnitude: maxRateObserved,
        limit: this.config.maxPlaybackRateAccel,
      })
    }
    if (maxRootAccelObserved > this.config.maxRootAcceleration * 1.001) {
      violations.push({
        kind: 'root-acceleration',
        magnitude: maxRootAccelObserved,
        limit: this.config.maxRootAcceleration,
      })
    }
    if (maxYawAccelObserved > this.config.maxYawAcceleration * 1.001) {
      violations.push({
        kind: 'yaw-acceleration',
        magnitude: maxYawAccelObserved,
        limit: this.config.maxYawAcceleration,
      })
    }

    const rawBlend = this.transitionSeconds > 0
      ? smoothstep01(this.transitionElapsed / this.transitionSeconds)
      : 1
    const transitionActive = this.transitionElapsed < this.transitionSeconds
    const transitionFloor = transitionActive
      ? nextFamily === 'locomotion' ? .14
      : nextFamily === 'combat' || nextFamily === 'air' ? .11
      : .08
      : 0
    const blend01 = Math.max(transitionFloor, rawBlend)

    return {
      state: nextState,
      family: nextFamily,
      dt,
      substeps,
      phase01: this.phase01,
      playbackRate: this.playbackRate,
      blend01,
      transitionSeconds: this.transitionSeconds,
      rootVelocity: { ...this.rootVelocity },
      yawRate: this.yawRate,
      stateChanged,
      phasePreserved,
      violations,
    }
  }
}
